import {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueFormatterFunc,
  ValueGetterParams,
} from "ag-grid-community";
import { ActionCellRenderer } from "./ActionCellRenderer";
import {
  CommodityThreshold,
  DefaultThreshold,
} from "@/interfaces/RiskThresholdTypes";

// Formatter to display risk thresholds with two decimal places
export const formatThresholdValue: ValueFormatterFunc = (params) => {
  if (!params?.data) return "";
  return Number(params.value).toFixed(2);
};

// Value getter for Commodity column that handles both types
const getCommodityName = (params: ValueGetterParams): string => {
  if (!params.data) return "";

  const data = params.data as CommodityThreshold | DefaultThreshold;

  // Check if it's a DefaultThreshold (pinned row)
  if ("thresholdDefaultId" in data && data.thresholdDefaultId != null) {
    return data.thresholdDefaultName || "";
  }

  // Otherwise it's a CommodityThreshold
  if ("scmCommodityNm" in data) {
    return (data as CommodityThreshold).scmCommodityNm || "";
  }

  return "";
};

export const RiskThresholdColumnDefinitions: (ColDef | ColGroupDef)[] = [
  {
    headerName: "",
    children: [
      {
        headerName: "",
        field: "isUsingDefault",
        width: 53,
        minWidth: 53,
        maxWidth: 53,
        resizable: false,
        suppressSizeToFit: true,
        suppressMovable: true,
        lockPosition: true,
        cellRenderer: (params: ICellRendererParams) =>
          params.value ? "✱" : "",
        cellStyle: {
          color: "rgb(0, 93, 164)",
          fontWeight: "bold",
          fontSize: "15px",
          textAlign: "left",
          borderRight: "1px solid #E5E7EB",
        },
      },
    ],
  },
  {
    headerName: "",
    children: [
      {
        headerName: "Commodity",
        valueGetter: getCommodityName,
        width: 180,
        suppressMovable: true,
        lockPosition: true,
        cellStyle: {
          borderRight: "1px solid #E5E7EB",
          textAlign: "left",
          paddingLeft: "8px",
        },
      },
    ],
  },
  {
    headerName: "High Risk",
    suppressMovable: true,
    children: [
      {
        headerName: "Min",
        field: "highMin",
        width: 90,
        headerClass: "header-red-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
      {
        headerName: "Max",
        field: "highMax",
        width: 90,
        headerClass: "header-red-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
    ],
  },
  {
    headerName: "Medium Risk",
    suppressMovable: true,
    children: [
      {
        headerName: "Min",
        field: "mediumMin",
        width: 90,
        headerClass: "header-yellow-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
      {
        headerName: "Max",
        field: "mediumMax",
        width: 90,
        headerClass: "header-yellow-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
    ],
  },
  {
    headerName: "Low Risk",
    suppressMovable: true,
    children: [
      {
        headerName: "Min",
        field: "lowMin",
        width: 90,
        headerClass: "header-green-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
      {
        headerName: "Max",
        field: "lowMax",
        width: 90,
        headerClass: "header-green-pill",
        suppressMovable: true,
        lockPosition: true,
        valueFormatter: formatThresholdValue,
      },
    ],
  },
  {
    headerName: "",
    children: [
      {
        headerName: "Action",
        field: "action",
        width: 80,
        suppressMovable: true,
        lockPosition: true,
        cellStyle: {
          borderRight: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
        },
        cellRenderer: ActionCellRenderer,
      },
    ],
  },
];
